import React from 'react';
import { AgentTemplate } from '../../../services/api';
import styles from './TemplateCard.module.css';

interface TemplateCardProps {
  template: AgentTemplate;
  onClick: (template: AgentTemplate) => void;
  featured?: boolean;
}

const categoryIcons: Record<string, string> = {
  'business-analysis': '📊',
  'product-management': '📦',
  'engineering': '⚙️',
  'design': '🎨',
  'marketing': '📣',
  'sales': '💼',
  'support': '🎧',
  'hr': '👥',
  'finance': '💰',
  'legal': '⚖️',
  'operations': '🔄',
  'data': '📈',
  'security': '🔒',
  'devops': '🚀',
  'qa': '🧪',
};

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onClick,
  featured = false,
}) => {
  const getCategoryIcon = (category: string) => {
    return categoryIcons[category] || '📋';
  };

  const formatCloneCount = (count: number | undefined | null) => {
    const safeCount = count ?? 0;
    if (safeCount >= 1000) {
      return `${(safeCount / 1000).toFixed(1)}k`;
    }
    return safeCount.toString();
  };

  const renderRating = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={`full-${i}`} className={styles.starFull}>★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className={styles.starHalf}>★</span>);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<span key={`empty-${i}`} className={styles.starEmpty}>☆</span>);
    }
    return stars;
  };

  return (
    <div 
      className={`${styles.card} ${featured ? styles.featured : ''}`}
      onClick={() => onClick(template)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(template)}
    >
      <div className={styles.header}>
        <div className={styles.icon}>
          {template.icon || getCategoryIcon(template.category)}
        </div>
        <div className={styles.headerContent}>
          <h3 className={styles.name}>{template.name}</h3>
          {(template.verified || template.isVerified) && (
            <span className={styles.verifiedBadge} title="Verified Template">
              ✓ Verified
            </span>
          )}
        </div>
      </div>

      <p className={styles.description}>
        {template.shortDescription || template.description?.substring(0, 150)}
        {(template.description?.length ?? 0) > 150 ? '...' : ''}
      </p>

      <div className={styles.tags}>
        {template.tags.slice(0, 4).map((tag) => (
          <span key={tag} className={styles.tag}>
            {tag}
          </span>
        ))}
        {template.tags.length > 4 && (
          <span className={styles.tagMore}>+{template.tags.length - 4}</span>
        )}
      </div>

      <div className={styles.capabilities}>
        <span className={styles.capabilityLabel}>Capabilities:</span>
        <span className={styles.capabilityCount}>
          {template.capabilities.length} features
        </span>
      </div>

      {template.integrations && template.integrations.length > 0 && (
        <div className={styles.integrations}>
          <span className={styles.integrationLabel}>Integrations:</span>
          <div className={styles.integrationIcons}>
            {template.integrations.slice(0, 3).map((integration) => (
              <span 
                key={integration.id} 
                className={styles.integrationIcon}
                title={integration.name}
              >
                {integration.icon}
              </span>
            ))}
            {template.integrations.length > 3 && (
              <span className={styles.integrationMore}>
                +{template.integrations.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <div className={styles.stats}>
          <div className={styles.rating}>
            {renderRating(template.rating ?? template.averageRating ?? 0)}
            <span className={styles.ratingValue}>{(template.rating ?? template.averageRating ?? 0).toFixed(1)}</span>
          </div>
          <span className={styles.clones}>
            {formatCloneCount(template.cloneCount ?? template.totalClones ?? 0)} clones
          </span>
        </div>

        {featured && (
          <span className={styles.featuredBadge}>⭐ Featured</span>
        )}
      </div>

      <div className={styles.hoverOverlay}>
        <span className={styles.viewDetails}>View Details →</span>
      </div>
    </div>
  );
};

export default TemplateCard;
